import React from "react";

const EXAMPLES = [
  {
    title: "ERC721 NFT Contract",
    standard: "ERC721",
    network: "Base Mainnet",
    modules: "Mintable, Burnable, Pausable",
    contract: "0x5e23b99c8DcF5f1Dbf521fbF1FABD78f9be7a0E5",
    tx: "0x35b65c10793dfa0ed6cecbab22c75203f05d81e02525b903a846b2e4ae241cd4",
    explorerBase: "https://basescan.org",
  },
  {
    title: "ERC1155 Multi-Token Contract",
    standard: "ERC1155",
    network: "Base Mainnet",
    modules: "Mintable, Burnable, Pausable",
    contract: "0x305B3638a19dd809B3842d7C43D82833D2A2a407",
    tx: "0x4b4effebf2904ce3596e02f4e4978dff8c3ea3f0c05a4887b2614badacc09aff",
    explorerBase: "https://basescan.org",
  },
  {
    title: "ERC20 Fungible Token",
    standard: "ERC20",
    network: "Polygon Mainnet",
    modules: "Mintable, Burnable, Pausable",
    contract: "0x5e23b99c8DcF5f1Dbf521fbF1FABD78f9be7a0E5",
    tx: "0x3214fb65225480df02bb07bb96872336371190e02cdd552ede94ffa612712b03",
    explorerBase: "https://polygonscan.com",
  },
  {
    title: "ERC721 NFT Contract",
    standard: "ERC721",
    network: "Arbitrum One",
    modules: "Mintable, Burnable, Pausable",
    contract: "0xC7a45c7565C2cf0203ff61B4ADb884faE73A3816",
    tx: "0x2f5906fe16a3baf0360311e1e624543487b8fff91cc507ec17bae5a0cc74efba",
    explorerBase: "https://arbiscan.io",
  },
  {
    title: "ERC1155 Multi-Token Contract",
    standard: "ERC1155",
    network: "OP Mainnet",
    modules: "Mintable, Burnable, Pausable",
    contract: "0xC7a45c7565C2cf0203ff61B4ADb884faE73A3816",
    tx: "0xaf50e2117f7902aa277ff9e09707604f43c01ba9f5d5d9106829ad4e6602f1ff",
    explorerBase: "https://optimistic.etherscan.io",
  },
  {
    title: "ERC20 Fungible Token",
    standard: "ERC20",
    network: "Avalanche C-Chain",
    modules: "Mintable, Burnable, Pausable",
    contract: "0x5e23b99c8DcF5f1Dbf521fbF1FABD78f9be7a0E5",
    tx: "0x9d90f91f94f91a7fc58208eb1fd5759ce9c54dc8abacd5b13465c250c656df23",
    explorerBase: "https://snowtrace.io",
  },
  {
    title: "ERC20 Fungible Token",
    standard: "ERC20",
    network: "BNB Smart Chain",
    modules: "Mintable, Burnable, Pausable",
    contract: "0xC7a45c7565C2cf0203ff61B4ADb884faE73A3816",
    tx: "0x0525af246a9429fef984e58bc22260e47a89033055518c7ffcf11e6a95d94649",
    explorerBase: "https://bscscan.com",
  },
];

function shortHash(value) {
  if (!value) return "-";
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

export default function Examples() {
  return (
    <div className="min-h-screen bg-[#F7F8FA] px-4 py-8 text-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="inline-flex rounded-full border border-[#C59B45]/30 bg-[#FFF8E1] px-3 py-1 text-xs font-semibold text-[#7A5A12]">
            Verified examples
          </div>

          <h1 className="mt-5 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Verified deployments made with Krionex.
          </h1>

          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
            Explore real smart contract deployments created through Krionex,
            including verified explorer records, deployment transactions,
            enabled modules, and public contract references across major EVM
            networks.
          </p>
        </section>

        <section className="grid gap-5 md:grid-cols-2">
          {EXAMPLES.map((item) => {
            const contractUrl = `${item.explorerBase}/address/${item.contract}`;
            const txUrl = `${item.explorerBase}/tx/${item.tx}`;

            return (
              <div
                key={`${item.network}-${item.standard}-${item.contract}`}
                className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8A6A1F]">
                      {item.network}
                    </div>

                    <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
                      {item.title}
                    </h2>
                  </div>

                  <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Verified
                  </div>
                </div>

                <div className="mt-5 space-y-3 text-sm leading-6 text-slate-600">
                  <div>
                    <span className="font-semibold text-slate-950">
                      Standard:
                    </span>{" "}
                    {item.standard}
                  </div>

                  <div>
                    <span className="font-semibold text-slate-950">
                      Modules:
                    </span>{" "}
                    {item.modules}
                  </div>

                  <div>
                    <span className="font-semibold text-slate-950">
                      Contract:
                    </span>{" "}
                    <span className="font-mono">{shortHash(item.contract)}</span>
                  </div>

                  <div>
                    <span className="font-semibold text-slate-950">
                      Deployment Tx:
                    </span>{" "}
                    <span className="font-mono">{shortHash(item.tx)}</span>
                  </div>

                  <div>
                    <span className="font-semibold text-slate-950">
                      Verification:
                    </span>{" "}
                    Verified on public explorer
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={contractUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    View Contract
                  </a>

                  <a
                    href={txUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-slate-50"
                  >
                    View Deployment Tx
                  </a>
                </div>
              </div>
            );
          })}
        </section>

        <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-900">
          These are public demo deployments used to verify Krionex workflows on
          block explorers. Do not send funds or valuable tokens to these example
          contracts.
        </div>
      </div>
    </div>
  );
}